import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import {
  IconDashboard,
  IconProxies,
  IconProxyChecker,
  IconClients,
  IconConfigurations,
  IconNetworkPorts,
  IconConnections,
  IconSimManager,
  IconMarketing,
  IconSettings,
  IconSupport,
  IconLogout,
} from '../sidebar-icons';
import {
  isSettingsPath,
  SETTINGS_CLIENT_CABINET_PATH,
  SETTINGS_LOGS_PATH,
  SETTINGS_MENU_KEY,
  SETTINGS_PROXY_MANAGER_PATH,
  SETTINGS_SERVICES_PATH,
  SETTINGS_BACKUPS_PATH,
  SETTINGS_SUPPORT_PATH,
} from '../../config/settings-routes';
import { CONNECTIONS_BASE_PATH, isConnectionsPath } from '../../config/connections-routes';
import './mobile-drawer.css';

/**
 * MobileDrawer — мобильное меню по макету Figma
 * Overlay справа, bg #383838, 296px шириной, border-radius-bl 12px
 */

const CHECKS_MENU_KEY = 'checks';
const CHECKS_CHILD_PATHS = ['/proxy-checker', '/location-check'];

const isChecksPath = (pathname) => CHECKS_CHILD_PATHS.includes(pathname);

const menuItems = [
  { key: '/', icon: <IconDashboard />, label: 'Дашборд' },
  { key: '/proxies', icon: <IconProxies />, label: 'Прокси' },
  {
    key: CHECKS_MENU_KEY,
    icon: <IconProxyChecker />,
    label: 'Проверки',
    children: [
      { key: '/proxy-checker', label: 'Проверка прокси' },
      { key: '/location-check', label: 'Проверка локаций' },
    ],
  },
  { key: '/clients', icon: <IconClients />, label: 'Клиенты' },
  { key: '/configurations', icon: <IconConfigurations />, label: 'Конфигурации' },
  { key: '/network-ports', icon: <IconNetworkPorts />, label: 'Сетевые порты' },
  { key: '/kraken-multi', icon: <IconConnections />, label: 'Подключения' },
  { key: '/sim-manager', icon: <IconSimManager />, label: 'SIM-менеджер' },
  { key: SETTINGS_SUPPORT_PATH, icon: <IconSupport />, label: 'Поддержка', staffOnly: true },
  { key: '/marketing', icon: <IconMarketing />, label: 'Маркетинг', adminOnly: true },
  {
    key: SETTINGS_MENU_KEY,
    icon: <IconSettings />,
    label: 'Настройки',
    adminOnly: true,
    children: [
      { key: SETTINGS_PROXY_MANAGER_PATH, label: 'Proxy Manager' },
      { key: SETTINGS_CLIENT_CABINET_PATH, label: 'Кабинет клиента' },
      { key: SETTINGS_LOGS_PATH, label: 'Логи' },
      { key: SETTINGS_SERVICES_PATH, label: 'Службы' },
      { key: SETTINGS_BACKUPS_PATH, label: 'Бэкапы' },
    ],
  },
];

const getInitialExpandedGroups = (pathname) => {
  const expanded = new Set();
  if (isSettingsPath(pathname)) {
    expanded.add(SETTINGS_MENU_KEY);
  }
  if (isChecksPath(pathname)) {
    expanded.add(CHECKS_MENU_KEY);
  }
  return expanded;
};

const MobileDrawer = ({
  open = false,
  onClose,
  onLogout,
  krakenConnected = false,
  isAdmin = false,
  isManager = false,
  supportUnread = 0,
}) => {
  const location = useLocation();
  const overlayRef = useRef(null);
  const [expandedGroups, setExpandedGroups] = useState(() => getInitialExpandedGroups(location.pathname));

  // Блокировка прокрутки body
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    setExpandedGroups((previous) => {
      const next = new Set(previous);
      if (isSettingsPath(location.pathname)) {
        next.add(SETTINGS_MENU_KEY);
      }
      if (isChecksPath(location.pathname)) {
        next.add(CHECKS_MENU_KEY);
      }
      return next;
    });
  }, [location.pathname]);

  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const handleNavLinkClick = (event) => {
    // Для middle/ctrl/cmd click оставляем нативное поведение браузера (новая вкладка)
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    onClose();
  };

  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    }
    onClose();
  };

  const handleGroupToggle = (groupKey) => {
    setExpandedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const handleGroupToggleKeyDown = (event, groupKey) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleGroupToggle(groupKey);
  };

  const visibleItems = menuItems.filter((item) => {
    if (item.staffOnly) return isAdmin || isManager;
    if (item.managerOnly) return isManager && !isAdmin;
    if (item.adminOnly) return isAdmin;
    return true;
  });

  const isItemActive = (itemKey) => {
    if (itemKey === SETTINGS_MENU_KEY) {
      return isSettingsPath(location.pathname);
    }

    if (itemKey === CHECKS_MENU_KEY) {
      return isChecksPath(location.pathname);
    }

    if (itemKey === CONNECTIONS_BASE_PATH) {
      return isConnectionsPath(location.pathname);
    }

    return location.pathname === itemKey;
  };

  return (
    <div
      ref={overlayRef}
      className={`mobile-drawer-overlay ${open ? 'mobile-drawer-overlay--open' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Навигационное меню"
    >
      <div className={`mobile-drawer ${open ? 'mobile-drawer--open' : ''}`}>
        {/* Header */}
        <div className="mobile-drawer__header">
          <img
            src="/logo-new.svg"
            fetchpriority="high"
            alt="LTE.Center Logo"
            className="mobile-drawer__logo"
          />
          <button
            type="button"
            className="mobile-drawer__close"
            onClick={onClose}
            aria-label="Закрыть меню"
            tabIndex={0}
          >
            <CloseOutlined style={{ fontSize: 20, color: '#fff' }} />
          </button>
        </div>

        {/* Menu */}
        <nav className="mobile-drawer__menu">
          {visibleItems.map((item) => {
            if (Array.isArray(item.children) && item.children.length > 0) {
              const isGroupActive = isItemActive(item.key);
              const isExpanded = expandedGroups.has(item.key);

              return (
                <div key={item.key} className="mobile-drawer__group">
                  <button
                    type="button"
                    className={`mobile-drawer__item mobile-drawer__item--group${isGroupActive ? ' mobile-drawer__item--active' : ''}`}
                    onClick={() => handleGroupToggle(item.key)}
                    onKeyDown={(event) => handleGroupToggleKeyDown(event, item.key)}
                    aria-expanded={isExpanded}
                    aria-label={item.label}
                    tabIndex={0}
                  >
                    <span className="mobile-drawer__item-icon">{item.icon}</span>
                    <span className="mobile-drawer__item-label">{item.label}</span>
                    <DownOutlined
                      className={`mobile-drawer__group-arrow${isExpanded ? ' mobile-drawer__group-arrow--expanded' : ''}`}
                    />
                  </button>

                  {isExpanded && (
                    <div className="mobile-drawer__submenu">
                      {item.children.map((child) => {
                        const isChildActive = location.pathname === child.key;

                        return (
                          <a
                            key={child.key}
                            href={child.key}
                            className={`mobile-drawer__item mobile-drawer__item--sub${isChildActive ? ' mobile-drawer__item--active' : ''}`}
                            onClick={handleNavLinkClick}
                            aria-current={isChildActive ? 'page' : undefined}
                            tabIndex={0}
                          >
                            <span className="mobile-drawer__item-label">
                              {child.label}
                              {child.key === SETTINGS_SUPPORT_PATH && supportUnread > 0 ? (
                                <span className="mobile-drawer__count">{supportUnread > 99 ? '99+' : supportUnread}</span>
                              ) : null}
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = isItemActive(item.key);

            return (
              <a
                key={item.key}
                href={item.key}
                className={`mobile-drawer__item ${isActive ? 'mobile-drawer__item--active' : ''}`}
                onClick={handleNavLinkClick}
                aria-current={isActive ? 'page' : undefined}
                tabIndex={0}
              >
                <span className="mobile-drawer__item-icon">{item.icon}</span>
                <span className="mobile-drawer__item-label">
                  {item.label}
                  {item.key === SETTINGS_SUPPORT_PATH && supportUnread > 0 ? (
                    <span className="mobile-drawer__count">{supportUnread > 99 ? '99+' : supportUnread}</span>
                  ) : null}
                </span>
              </a>
            );
          })}
        </nav>

        {/* Status */}
        <div className="mobile-drawer__status">
          <div className="mobile-drawer__status-row">
            <span
              className={`mobile-drawer__status-dot ${krakenConnected ? '' : 'mobile-drawer__status-dot--disconnected'}`}
            />
            <span className="mobile-drawer__status-text">
              {krakenConnected ? 'Подключен' : 'Отключен'}
            </span>
          </div>
        </div>

        {/* Logout */}
        <div className="mobile-drawer__footer">
          <button
            type="button"
            className="mobile-drawer__item"
            onClick={handleLogoutClick}
            aria-label="Выйти из системы"
            tabIndex={0}
          >
            <span className="mobile-drawer__item-icon">
              <IconLogout />
            </span>
            <span className="mobile-drawer__item-label">Выйти</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileDrawer;
